import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphPlanetaryActivation } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { createHdGateHoverHighlight } from '../../hd-gate-hover-highlight';
import { HdPlanetActivationBlockClickEvent } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HdFixingState } from '../hd-planet-display-config';

export type HdJustNowPlanetCellKind = 'design' | 'personality' | 'transit';

@Component({
  selector: 'hd-just-now-planet-activation-cell',
  templateUrl: './hd-just-now-planet-activation-cell.component.html',
  styleUrl: './hd-just-now-planet-activation-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-design]': 'kind() === "design"',
    '[class.is-personality]': 'kind() === "personality"',
    '[class.is-transit]': 'kind() === "transit"',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
  },
})
export class HdJustNowPlanetActivationCellComponent {
  readonly activation = input<HdBodygraphPlanetaryActivation | null>(null);
  readonly kind = input.required<HdJustNowPlanetCellKind>();
  readonly fixing = input<HdFixingState>('none');
  readonly transitFixing = input<HdFixingState>('none');
  readonly transitTooltip = input('');
  readonly contextMenuEnabled = input(true);

  readonly activationClicked = output<HdPlanetActivationBlockClickEvent>();
  readonly contextMenuRequested = output<{ gate: number; position: { x: number; y: number } }>();

  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly hostHovered = signal(false);

  protected readonly showExaltationArrow = computed(
    () => this.fixing() === 'exaltation' || this.fixing() === 'both',
  );
  protected readonly showDetrimentArrow = computed(
    () => this.fixing() === 'detriment' || this.fixing() === 'both',
  );
  protected readonly showTransitExaltationArrow = computed(
    () => this.transitFixing() === 'exaltation' || this.transitFixing() === 'both',
  );
  protected readonly showTransitDetrimentArrow = computed(
    () => this.transitFixing() === 'detriment' || this.transitFixing() === 'both',
  );

  /** Gedeelde hover-regels (match / bron / dim); een lege cel doet niet mee. */
  private readonly gateHover = createHdGateHoverHighlight(
    this.hoverService,
    () => this.activation()?.gate ?? null,
    this.hostHovered,
  );
  protected readonly isHoverMatch = this.gateHover.isMatch;
  protected readonly isHoverSource = this.gateHover.isSource;
  protected readonly isHoverDimmed = this.gateHover.isDimmed;

  protected handleMouseEnter(): void {
    const activation = this.activation();
    if (activation == null) {
      return;
    }
    this.hostHovered.set(true);
    this.hoverService?.setGateHover(activation.gate, 'planet-grid-block');
  }

  protected handleMouseLeave(): void {
    this.hostHovered.set(false);
    this.hoverService?.setGateHover(null, 'planet-grid-block');
  }

  protected handleClick(event: MouseEvent): void {
    const activation = this.activation();
    if (activation == null) {
      return;
    }
    event.stopPropagation();
    if ((event.ctrlKey || event.metaKey) && this.hoverService?.enabled()) {
      this.hoverService.toggleFreezeForTarget({ kind: 'gate', gate: activation.gate });
      return;
    }
    this.activationClicked.emit({
      activation,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  protected handleContextMenu(event: MouseEvent): void {
    const activation = this.activation();
    if (activation == null || !this.contextMenuEnabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.hoverService?.suspendForContextMenu();
    this.contextMenuRequested.emit({
      gate: activation.gate,
      position: { x: event.clientX, y: event.clientY },
    });
  }
}
